import axios, { AxiosInstance, AxiosError } from 'axios';
import type {
  ApiResponse,
  AuthResponse,
  LoginCredentials,
  Ticket,
  TicketAgent,
  TicketAttachment,
  TicketTimeline,
  CreateCommentDto,
  WorkLog,
  CreateWorkLogDto,
  CreateTicketDto,
  User,
  Organization,
  DashboardSummary,
  BillingSummary,
  AuditLog,
  VoiceEntry,
  Project,
  Milestone,
  ProjectStats,
  Notification,
  ApiKeyInfo,
  WebhookEndpoint,
  WebhookDelivery,
  SearchResponse,
  BurndownDataPoint,
  BudgetVsActualData,
  MonthlyRevenuePoint,
  HoursByAgentData,
  TicketsByPriorityData,
  HoursByTicketData,
  AgentSummaryData,
  IntegrationPreset,
  IntegrationInstance,
  IntegrationFieldMapping,
  IntegrationPipelineStep,
  IntegrationTarget,
  IntegrationSecretKey,
  AttendanceRecord,
  AttendanceDailySummary,
  AttendancePolicy,
  AttendanceStatus,
  AttendanceVsTicketsRow,
  IntegrationInboundLog,
  TestPipelineResponse,
  BlocklistRule,
  EmailTemplate,
  EmailTemplateMergeSchema,
  EmailTemplatePreview,
  EmailTemplateAsset,
  Lead,
  LeadProduct,
  LeadCommunication,
  Product,
  Quote,
  QuoteLineItem,
  Dashboard,
  DashboardWidget,
  WidgetCatalogItem,
  Contact,
  ContactInteraction,
} from '../types';

const API_URL = import.meta.env.VITE_API_URL || '/api';

class ApiService {
  private client: AxiosInstance;

  constructor() {
    this.client = axios.create({
      baseURL: API_URL,
      headers: {
        'Content-Type': 'application/json',
      },
      timeout: 30000,
    });

    this.client.interceptors.request.use(
      (config) => {
        const token = localStorage.getItem('auth_token');
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
      },
      (error) => Promise.reject(error)
    );

    this.client.interceptors.response.use(
      (response) => response.data,
      (error: AxiosError<ApiResponse<any>>) => {
        if (error.response?.status === 401) {
          const currentToken = localStorage.getItem('auth_token');
          const reqAuth = error.config?.headers?.Authorization;
          const isStale = currentToken && reqAuth && reqAuth !== `Bearer ${currentToken}`;
          if (!isStale && !window.location.pathname.startsWith('/login')) {
            localStorage.removeItem('auth_token');
            localStorage.removeItem('user');
            window.location.href = '/login';
          }
        }
        return Promise.reject(error.response?.data || error);
      }
    );
  }

  // Auth
  async login(credentials: LoginCredentials): Promise<ApiResponse<AuthResponse>> {
    return this.client.post('/login', credentials);
  }

  async impersonateUser(userId: string): Promise<ApiResponse<AuthResponse & { impersonated_by: { id: string; email: string } }>> {
    return this.client.post(`/impersonate/${userId}`);
  }

  // Tickets
  async getTickets(params?: {
    status?: string;
    priority?: string;
    search?: string;
    customer_id?: string;
    agent_id?: string;
    filter?: string;
    organization_id?: string;
    project_id?: string;
    limit?: number;
    offset?: number;
  }): Promise<ApiResponse<Ticket[]>> {
    return this.client.get('/tickets', { params });
  }

  async updateTicketPreferences(prefs: {
    assigned?: boolean;
    commented?: boolean;
    created?: boolean;
    include_closed?: boolean;
  }): Promise<ApiResponse<any>> {
    return this.client.patch('/profile/ticket-preferences', prefs);
  }

  async getTicket(id: string): Promise<ApiResponse<Ticket>> {
    return this.client.get(`/tickets/${id}`);
  }

  async createTicket(data: CreateTicketDto): Promise<ApiResponse<Ticket>> {
    return this.client.post('/tickets', data);
  }

  async updateTicket(id: string, data: Partial<{
    title: string;
    description: string;
    priority: string;
    assigned_agent_id: string | null;
    status: string;
    due_date: string | null;
  }>): Promise<ApiResponse<Ticket>> {
    return this.client.patch(`/tickets/${id}`, data);
  }

  async updateTicketStatus(id: string, status: string, cascadeCloseSubtasks?: boolean): Promise<ApiResponse<Ticket>> {
    return this.client.patch(`/tickets/${id}/status`, {
      status,
      ...(cascadeCloseSubtasks !== undefined && { cascade_close_subtasks: cascadeCloseSubtasks }),
    });
  }

  async deleteTicket(id: string): Promise<ApiResponse<void>> {
    return this.client.delete(`/tickets/${id}`);
  }

  async assignTicket(id: string, agentId: string): Promise<ApiResponse<Ticket>> {
    return this.client.patch(`/tickets/${id}/assign`, { agent_id: agentId });
  }

  async getTicketAgents(ticketId: string): Promise<ApiResponse<TicketAgent[]>> {
    return this.client.get(`/tickets/${ticketId}/agents`);
  }

  async addTicketAgent(ticketId: string, agentId: string): Promise<ApiResponse<TicketAgent[]>> {
    return this.client.post(`/tickets/${ticketId}/agents`, { agent_id: agentId });
  }

  async removeTicketAgent(ticketId: string, agentId: string): Promise<ApiResponse<void>> {
    return this.client.delete(`/tickets/${ticketId}/agents/${agentId}`);
  }

  // Timeline
  async getTicketTimeline(ticketId: string): Promise<ApiResponse<TicketTimeline>> {
    return this.client.get(`/tickets/${ticketId}/timeline`);
  }

  // Comments
  async createComment(ticketId: string, data: CreateCommentDto): Promise<ApiResponse<any>> {
    return this.client.post(`/tickets/${ticketId}/comments`, data);
  }

  async updateComment(ticketId: string, commentId: string, content: string): Promise<ApiResponse<any>> {
    return this.client.patch(`/tickets/${ticketId}/comments/${commentId}`, { content });
  }

  async deleteComment(ticketId: string, commentId: string): Promise<ApiResponse<void>> {
    return this.client.delete(`/tickets/${ticketId}/comments/${commentId}`);
  }

  // Attachments
  async uploadTicketAttachment(
    ticketId: string,
    file: File,
    onProgress?: (pct: number) => void
  ): Promise<ApiResponse<TicketAttachment>> {
    const formData = new FormData();
    formData.append('file', file);
    return this.client.post(`/tickets/${ticketId}/attachments`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      onUploadProgress: (e) => onProgress && onProgress(Math.round((e.loaded * 100) / (e.total || 1))),
    });
  }

  async uploadCommentAttachment(
    ticketId: string,
    commentId: string,
    file: File
  ): Promise<ApiResponse<TicketAttachment>> {
    const formData = new FormData();
    formData.append('file', file);
    return this.client.post(`/tickets/${ticketId}/comments/${commentId}/attachments`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  }

  async deleteAttachment(ticketId: string, attachmentId: string): Promise<ApiResponse<void>> {
    return this.client.delete(`/tickets/${ticketId}/attachments/${attachmentId}`);
  }

  // Create ticket with files (multipart)
  async createTicketWithFiles(
    data: CreateTicketDto & { description?: string; priority?: string },
    files: File[]
  ): Promise<ApiResponse<Ticket>> {
    const formData = new FormData();
    formData.append('customer_id', data.customer_id);
    formData.append('title', data.title);
    if (data.description) formData.append('description', data.description);
    if (data.priority) formData.append('priority', data.priority);
    if (data.assigned_agent_id) formData.append('assigned_agent_id', data.assigned_agent_id);
    if (data.task_type) formData.append('task_type', data.task_type);
    if (data.contact_id) formData.append('contact_id', data.contact_id);
    if (data.due_date) formData.append('due_date', data.due_date);
    files.forEach((f) => formData.append('files', f));
    return this.client.post('/tickets', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  }

  // Work Logs
  async getWorkLogs(params?: {
    ticket_id?: string;
    agent_id?: string;
    start_date?: string;
    end_date?: string;
    requires_review?: boolean;
    limit?: number;
    offset?: number;
  }): Promise<ApiResponse<WorkLog[]>> {
    return this.client.get('/work-logs', { params });
  }

  async getWorkLog(id: string): Promise<ApiResponse<WorkLog>> {
    return this.client.get(`/work-logs/${id}`);
  }

  async createWorkLog(data: CreateWorkLogDto): Promise<ApiResponse<WorkLog>> {
    return this.client.post('/work-logs', data);
  }

  async updateWorkLog(id: string, data: Partial<CreateWorkLogDto>): Promise<ApiResponse<WorkLog>> {
    return this.client.patch(`/work-logs/${id}`, data);
  }

  async deleteWorkLog(id: string): Promise<ApiResponse<void>> {
    return this.client.delete(`/work-logs/${id}`);
  }

  async reviewWorkLog(id: string): Promise<ApiResponse<WorkLog>> {
    return this.client.patch(`/work-logs/${id}/review`);
  }

  async batchReviewWorkLogs(ids: string[]): Promise<ApiResponse<{ reviewed_count: number }>> {
    return this.client.post('/work-logs/batch-review', { ids });
  }

  // Users
  async getUsers(params?: {
    organization_id?: string;
    role?: string;
    limit?: number;
    offset?: number;
  }): Promise<ApiResponse<(User & { organization_name?: string })[]>> {
    return this.client.get('/users', { params });
  }

  async createUser(data: {
    organization_id: string;
    role: string;
    name: string;
    email: string;
    password: string;
    phone?: string;
    phone_extension?: string;
  }): Promise<ApiResponse<User>> {
    return this.client.post('/users', data);
  }

  async importUsers(organizationId: string, file: File, defaultRole?: string, defaultPassword?: string): Promise<ApiResponse<{ created: number; skipped: number; total: number; details: any[] }>> {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('organization_id', organizationId);
    if (defaultRole) formData.append('default_role', defaultRole);
    if (defaultPassword) formData.append('default_password', defaultPassword);
    return this.client.post('/users/import', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  }

  async updateUser(id: string, data: Partial<{
    name: string;
    email: string;
    role: string;
    phone: string;
    phone_extension: string | null;
    active: boolean;
    password: string;
  }>): Promise<ApiResponse<User>> {
    return this.client.patch(`/users/${id}`, data);
  }

  async deleteUser(id: string): Promise<ApiResponse<void>> {
    return this.client.delete(`/users/${id}`);
  }

  // Organizations
  async getOrganizations(params?: { limit?: number; offset?: number }): Promise<ApiResponse<Organization[]>> {
    return this.client.get('/organizations', { params });
  }

  async createOrganization(data: {
    name: string;
    billing_model: string;
    hourly_rate?: number;
    retainer_hours?: number;
    account_manager_id?: string | null;
    tax_id?: string;
    email?: string;
    phone?: string;
    address?: string;
    city?: string;
    website?: string;
    notes?: string;
  }): Promise<ApiResponse<Organization>> {
    return this.client.post('/organizations', data);
  }

  async updateOrganization(id: string, data: Partial<{
    name: string;
    billing_model: string;
    hourly_rate: number;
    retainer_hours: number;
    active: boolean;
    account_manager_id: string | null;
    tax_id: string;
    email: string;
    phone: string;
    address: string;
    city: string;
    website: string;
    notes: string;
  }>): Promise<ApiResponse<Organization>> {
    return this.client.patch(`/organizations/${id}`, data);
  }

  // Dashboard
  async getDashboardSummary(): Promise<ApiResponse<DashboardSummary>> {
    return this.client.get('/dashboard/admin-summary');
  }

  async getBilling(params?: { month?: string; organization_id?: string }): Promise<ApiResponse<BillingSummary[]>> {
    return this.client.get('/dashboard/billing', { params });
  }

  async getHoursByOrg(): Promise<ApiResponse<{ organization_name: string; total_hours: number; total_amount: number }[]>> {
    return this.client.get('/dashboard/hours-by-org');
  }

  async getMonthlyTrend(): Promise<ApiResponse<{ month: string; total_hours: number; billable_hours: number; log_count: number }[]>> {
    return this.client.get('/dashboard/monthly-trend');
  }

  // Profile
  async getProfile(): Promise<ApiResponse<User & { organization_name: string }>> {
    return this.client.get('/profile');
  }

  async updateProfile(data: {
    name?: string;
    phone?: string;
    email_notifications?: boolean;
    current_password?: string;
    new_password?: string;
  }): Promise<ApiResponse<User>> {
    return this.client.patch('/profile', data);
  }

  // Audit
  async getAuditLogs(params?: {
    work_log_id?: string;
    changed_by?: string;
    change_type?: string;
    start_date?: string;
    end_date?: string;
    limit?: number;
    offset?: number;
  }): Promise<ApiResponse<AuditLog[]>> {
    return this.client.get('/audit', { params });
  }

  // Voice
  async uploadVoice(file: File): Promise<ApiResponse<{ voice_entry: VoiceEntry; work_log?: WorkLog; message: string }>> {
    const formData = new FormData();
    formData.append('audio', file);
    return this.client.post('/voice/upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 120000,
    });
  }

  async getVoiceEntries(params?: { status?: string; limit?: number; offset?: number }): Promise<ApiResponse<VoiceEntry[]>> {
    return this.client.get('/voice', { params });
  }

  // Projects
  async getProjects(params?: {
    organization_id?: string;
    status?: string;
    type?: string;
    limit?: number;
    offset?: number;
  }): Promise<ApiResponse<(Project & { ticket_count: number; open_ticket_count: number; total_actual_hours: number; organization_name: string; overdue_ticket_count: number })[]>> {
    return this.client.get('/projects', { params });
  }

  async getProject(id: string): Promise<ApiResponse<Project & { milestones: (Milestone & { tickets_total: number; tickets_closed: number; total_actual_hours: number })[]; stats: ProjectStats; organization_name: string }>> {
    return this.client.get(`/projects/${id}`);
  }

  async createProject(data: {
    organization_id: string;
    name: string;
    type: string;
    description?: string;
    customer_summary?: string;
    billing_model?: string;
    hourly_rate?: number;
    fixed_price?: number;
    total_budget_hours?: number;
    renewal_type?: string;
    renewal_day?: number;
    show_hours_to_customer?: boolean;
    start_date?: string;
    end_date?: string;
    deadline_warning_days?: number;
  }): Promise<ApiResponse<Project>> {
    return this.client.post('/projects', data);
  }

  async updateProject(id: string, data: Partial<Project>): Promise<ApiResponse<Project>> {
    return this.client.patch(`/projects/${id}`, data);
  }

  async deleteProject(id: string): Promise<ApiResponse<void>> {
    return this.client.delete(`/projects/${id}`);
  }

  async getProjectTickets(projectId: string, params?: {
    status?: string; limit?: number; offset?: number;
  }): Promise<ApiResponse<Ticket[]>> {
    return this.client.get(`/projects/${projectId}/tickets`, { params });
  }

  async duplicateMilestone(projectId: string, milestoneId: string, targetProjectId: string): Promise<ApiResponse<Milestone>> {
    return this.client.post(`/projects/${projectId}/duplicate-milestone`, {
      milestone_id: milestoneId,
      target_project_id: targetProjectId,
    });
  }

  // Milestones
  async getMilestones(projectId: string): Promise<ApiResponse<(Milestone & { tickets_total: number; tickets_closed: number; total_estimated_hours: number; total_actual_hours: number })[]>> {
    return this.client.get(`/projects/${projectId}/milestones`);
  }

  async createMilestone(projectId: string, data: {
    name: string;
    description?: string;
    due_date?: string;
    estimated_hours?: number;
  }): Promise<ApiResponse<Milestone>> {
    return this.client.post(`/projects/${projectId}/milestones`, data);
  }

  async getMilestone(id: string): Promise<ApiResponse<Milestone & { project_name: string; organization_name: string; organization_id: string; deadline_warning_days: number }>> {
    return this.client.get(`/milestones/${id}`);
  }

  async updateMilestone(id: string, data: Partial<Milestone>): Promise<ApiResponse<Milestone>> {
    return this.client.patch(`/milestones/${id}`, data);
  }

  async completeMilestone(id: string, notify: 'none' | 'admin' | 'customer' = 'none'): Promise<ApiResponse<Milestone>> {
    return this.client.patch(`/milestones/${id}/complete`, { notify });
  }

  async deleteMilestone(id: string): Promise<ApiResponse<void>> {
    return this.client.delete(`/milestones/${id}`);
  }

  async reorderMilestones(projectId: string, items: { id: string; order_index: number }[]): Promise<ApiResponse<void>> {
    return this.client.patch(`/projects/${projectId}/milestones/reorder`, items);
  }

  async getMilestoneTickets(milestoneId: string, params?: {
    limit?: number; offset?: number;
  }): Promise<ApiResponse<Ticket[]>> {
    return this.client.get(`/milestones/${milestoneId}/tickets`, { params });
  }

  // Organization details & tickets
  async getOrganization(id: string): Promise<ApiResponse<Organization & { project_count: number; ticket_count: number; user_count: number }>> {
    return this.client.get(`/organizations/${id}`);
  }

  async getOrganizationTickets(orgId: string, params?: {
    project_id?: string;
    status?: string;
    direct_only?: string;
    limit?: number;
    offset?: number;
  }): Promise<ApiResponse<Ticket[]>> {
    return this.client.get(`/organizations/${orgId}/tickets`, { params });
  }

  // Ticket hierarchy
  async moveTicket(ticketId: string, data: {
    project_id?: string | null;
    milestone_id?: string | null;
    hours_action?: 'move' | 'keep' | 'split';
  }): Promise<ApiResponse<Ticket>> {
    return this.client.patch(`/tickets/${ticketId}/move`, data);
  }

  async getSubtasks(ticketId: string): Promise<ApiResponse<Ticket[]>> {
    return this.client.get(`/tickets/${ticketId}/subtasks`);
  }

  async createSubtask(ticketId: string, data: {
    title: string;
    description?: string;
    priority?: string;
    estimated_hours?: number;
    assigned_agent_id?: string;
  }): Promise<ApiResponse<Ticket>> {
    return this.client.post(`/tickets/${ticketId}/subtasks`, data);
  }

  // Notifications
  async getNotifications(): Promise<ApiResponse<Notification[]>> {
    return this.client.get('/notifications');
  }

  async markNotificationRead(id: string): Promise<ApiResponse<void>> {
    return this.client.patch(`/notifications/${id}/read`);
  }

  async markAllNotificationsRead(): Promise<ApiResponse<void>> {
    return this.client.post('/notifications/read-all');
  }

  // Timer
  async startTimer(ticketId: string): Promise<ApiResponse<any>> {
    return this.client.post('/timer/start', { ticket_id: ticketId });
  }

  async stopTimer(): Promise<ApiResponse<{ start_time: string; end_time: string; duration_minutes: number; ticket_id: string; ticket_title: string }>> {
    return this.client.post('/timer/stop');
  }

  async getTimerStatus(): Promise<ApiResponse<{ timer_started_at: string; timer_ticket_id: string; timer_status: string; ticket_title: string } | null>> {
    return this.client.get('/timer/status');
  }

  // Export URLs
  getExportWorkLogsUrl(params?: { start_date?: string; end_date?: string; organization_id?: string }): string {
    const sp = new URLSearchParams();
    if (params?.start_date) sp.set('start_date', params.start_date);
    if (params?.end_date) sp.set('end_date', params.end_date);
    if (params?.organization_id) sp.set('organization_id', params.organization_id);
    return `${API_URL}/export/work-logs?${sp.toString()}`;
  }

  getExportBillingUrl(params?: { month?: string }): string {
    const sp = new URLSearchParams();
    if (params?.month) sp.set('month', params.month);
    return `${API_URL}/export/billing?${sp.toString()}`;
  }

  // --- API Keys ---
  async getApiKeys(): Promise<ApiResponse<ApiKeyInfo[]>> {
    return this.client.get('/settings/api-keys');
  }

  async createApiKey(data: {
    name: string;
    scopes: string[];
    scope_type?: string;
    scoped_org_id?: string;
    organization_id?: string;
  }): Promise<ApiResponse<ApiKeyInfo>> {
    return this.client.post('/settings/api-keys', data);
  }

  async revokeApiKey(id: string): Promise<ApiResponse<void>> {
    return this.client.delete(`/settings/api-keys/${id}`);
  }

  // --- Webhooks ---
  async getWebhooks(): Promise<ApiResponse<WebhookEndpoint[]>> {
    return this.client.get('/settings/webhooks');
  }

  async createWebhook(data: {
    url: string;
    events: string[];
    organization_id?: string;
  }): Promise<ApiResponse<WebhookEndpoint & { secret: string }>> {
    return this.client.post('/settings/webhooks', data);
  }

  async updateWebhook(id: string, data: Partial<{
    url: string;
    events: string[];
    active: boolean;
  }>): Promise<ApiResponse<WebhookEndpoint>> {
    return this.client.patch(`/settings/webhooks/${id}`, data);
  }

  async deleteWebhook(id: string): Promise<ApiResponse<void>> {
    return this.client.delete(`/settings/webhooks/${id}`);
  }

  async getWebhookDeliveries(webhookId: string, params?: {
    limit?: number;
    offset?: number;
  }): Promise<ApiResponse<WebhookDelivery[]>> {
    return this.client.get(`/settings/webhooks/${webhookId}/deliveries`, { params });
  }

  async testWebhook(webhookId: string): Promise<ApiResponse<WebhookDelivery>> {
    return this.client.post(`/settings/webhooks/${webhookId}/test`);
  }

  // --- Search ---
  async search(params: {
    q: string;
    types?: string;
    org?: string;
    from?: string;
    to?: string;
    agent?: string;
  }): Promise<ApiResponse<SearchResponse>> {
    return this.client.get('/search', { params });
  }

  // --- Reports ---
  async getProjectBurndown(projectId: string, params?: {
    from?: string;
    to?: string;
  }): Promise<ApiResponse<BurndownDataPoint[]>> {
    return this.client.get(`/projects/${projectId}/burndown`, { params });
  }

  async getProjectBudgetActual(projectId: string): Promise<ApiResponse<BudgetVsActualData>> {
    return this.client.get(`/projects/${projectId}/budget-actual`);
  }

  async getMonthlyRevenue(): Promise<ApiResponse<MonthlyRevenuePoint[]>> {
    return this.client.get('/reports/monthly-revenue');
  }

  async getHoursByAgent(month?: string): Promise<ApiResponse<HoursByAgentData[]>> {
    return this.client.get('/reports/hours-by-agent', { params: month ? { month } : undefined });
  }

  async getTicketsByPriority(): Promise<ApiResponse<TicketsByPriorityData[]>> {
    return this.client.get('/reports/tickets-by-priority');
  }

  async getHoursByTicket(orgId?: string): Promise<ApiResponse<HoursByTicketData[]>> {
    return this.client.get('/reports/hours-by-ticket', { params: orgId ? { org_id: orgId } : undefined });
  }

  async getAgentSummary(): Promise<ApiResponse<AgentSummaryData>> {
    return this.client.get('/reports/agent-summary');
  }

  // --- Language ---
  async updateLanguage(language: string): Promise<ApiResponse<User>> {
    return this.client.patch('/profile', { preferred_language: language });
  }

  // --- Integration Presets ---
  async getIntegrationPresets(): Promise<ApiResponse<IntegrationPreset[]>> {
    return this.client.get('/integrations/presets');
  }

  // --- Integration Instances ---
  async getIntegrations(): Promise<ApiResponse<IntegrationInstance[]>> {
    return this.client.get('/integrations');
  }

  async getIntegration(id: string): Promise<ApiResponse<IntegrationInstance>> {
    return this.client.get(`/integrations/${id}`);
  }

  async createIntegration(data: {
    key: string;
    name: string;
    provider_type: string;
    preset_key?: string;
    source_type: string;
    source_config?: Record<string, any>;
    notes?: string;
  }): Promise<ApiResponse<IntegrationInstance>> {
    return this.client.post('/integrations', data);
  }

  async updateIntegration(id: string, data: Partial<{
    name: string;
    source_config: Record<string, any>;
    enabled: boolean;
    notes: string;
  }>): Promise<ApiResponse<IntegrationInstance>> {
    return this.client.patch(`/integrations/${id}`, data);
  }

  async deleteIntegration(id: string): Promise<ApiResponse<void>> {
    return this.client.delete(`/integrations/${id}`);
  }

  // --- Field Mappings ---
  async getIntegrationMappings(instanceId: string): Promise<ApiResponse<IntegrationFieldMapping[]>> {
    return this.client.get(`/integrations/${instanceId}/mappings`);
  }

  async createIntegrationMapping(instanceId: string, data: {
    source_field_key: string;
    target_field_key: string;
    jsonpath_expression: string;
    is_required?: boolean;
    default_value?: string;
  }): Promise<ApiResponse<IntegrationFieldMapping>> {
    return this.client.post(`/integrations/${instanceId}/mappings`, data);
  }

  async deleteIntegrationMapping(instanceId: string, mappingId: string): Promise<ApiResponse<void>> {
    return this.client.delete(`/integrations/${instanceId}/mappings/${mappingId}`);
  }

  // --- Pipeline Steps ---
  async getIntegrationSteps(instanceId: string): Promise<ApiResponse<IntegrationPipelineStep[]>> {
    return this.client.get(`/integrations/${instanceId}/steps`);
  }

  async createIntegrationStep(instanceId: string, data: {
    step_order: number;
    step_type: string;
    config_json?: Record<string, any>;
    enabled?: boolean;
  }): Promise<ApiResponse<IntegrationPipelineStep>> {
    return this.client.post(`/integrations/${instanceId}/steps`, data);
  }

  async updateIntegrationStep(instanceId: string, stepId: string, data: Partial<{
    step_order: number;
    config_json: Record<string, any>;
    enabled: boolean;
  }>): Promise<ApiResponse<IntegrationPipelineStep>> {
    return this.client.patch(`/integrations/${instanceId}/steps/${stepId}`, data);
  }

  async swapIntegrationStepOrder(instanceId: string, stepIdA: string, stepIdB: string): Promise<ApiResponse<IntegrationPipelineStep[]>> {
    return this.client.post(`/integrations/${instanceId}/steps/swap`, { stepIdA, stepIdB });
  }

  async deleteIntegrationStep(instanceId: string, stepId: string): Promise<ApiResponse<void>> {
    return this.client.delete(`/integrations/${instanceId}/steps/${stepId}`);
  }

  // --- Targets ---
  async getIntegrationTargets(instanceId: string): Promise<ApiResponse<IntegrationTarget[]>> {
    return this.client.get(`/integrations/${instanceId}/targets`);
  }

  async createIntegrationTarget(instanceId: string, data: {
    target_order: number;
    target_type: string;
    field_bindings?: Record<string, any>;
    enabled?: boolean;
  }): Promise<ApiResponse<IntegrationTarget>> {
    return this.client.post(`/integrations/${instanceId}/targets`, data);
  }

  async updateIntegrationTarget(instanceId: string, targetId: string, data: Partial<{
    target_order: number;
    target_type: string;
    field_bindings: Record<string, any>;
    enabled: boolean;
  }>): Promise<ApiResponse<IntegrationTarget>> {
    return this.client.patch(`/integrations/${instanceId}/targets/${targetId}`, data);
  }

  async deleteIntegrationTarget(instanceId: string, targetId: string): Promise<ApiResponse<void>> {
    return this.client.delete(`/integrations/${instanceId}/targets/${targetId}`);
  }

  // --- Secrets ---
  async setIntegrationSecret(instanceId: string, key: string, value: string): Promise<ApiResponse<void>> {
    return this.client.put(`/integrations/${instanceId}/secrets`, { key_name: key, secret_value: value });
  }

  async deleteIntegrationSecret(instanceId: string, key: string): Promise<ApiResponse<void>> {
    return this.client.delete(`/integrations/${instanceId}/secrets/${key}`);
  }

  async getIntegrationSecretKeys(instanceId: string): Promise<ApiResponse<IntegrationSecretKey[]>> {
    return this.client.get(`/integrations/${instanceId}/secrets`);
  }

  // --- Integration Logs ---
  async getIntegrationLogs(instanceId: string, params?: {
    status?: string;
    limit?: number;
    offset?: number;
  }): Promise<ApiResponse<IntegrationInboundLog[]>> {
    return this.client.get(`/integrations/${instanceId}/logs`, { params });
  }

  async getIntegrationLogDetail(instanceId: string, logId: string): Promise<ApiResponse<IntegrationInboundLog>> {
    return this.client.get(`/integrations/${instanceId}/logs/${logId}`);
  }

  // --- Test Pipeline ---
  async testIntegrationPipeline(instanceId: string, payload: Record<string, any>): Promise<ApiResponse<TestPipelineResponse>> {
    return this.client.post(`/integrations/${instanceId}/test`, { payload });
  }

  // --- Integration Approval Workflow ---
  async submitIntegration(instanceId: string): Promise<ApiResponse<IntegrationInstance>> {
    return this.client.post(`/integrations/${instanceId}/submit`);
  }

  async approveIntegration(instanceId: string): Promise<ApiResponse<IntegrationInstance>> {
    return this.client.post(`/integrations/${instanceId}/approve`);
  }

  async rejectIntegration(instanceId: string, rejection_reason?: string): Promise<ApiResponse<IntegrationInstance>> {
    return this.client.post(`/integrations/${instanceId}/reject`, { rejection_reason });
  }

  // --- Gmail Add-on API key ---
  async getIntegrationAddonKey(instanceId: string): Promise<ApiResponse<{
    has_key: boolean;
    prefix: string | null;
    created_at: string | null;
    last_used_at: string | null;
  }>> {
    return this.client.get(`/integrations/${instanceId}/addon-key`);
  }

  async createIntegrationAddonKey(instanceId: string): Promise<ApiResponse<{
    api_key: string;
    prefix: string;
    created_at: string;
    warning: string;
  }>> {
    return this.client.post(`/integrations/${instanceId}/addon-key`);
  }

  async deleteIntegrationAddonKey(instanceId: string): Promise<ApiResponse<void>> {
    return this.client.delete(`/integrations/${instanceId}/addon-key`);
  }

  // --- Blocklist ---
  async getBlocklistRules(): Promise<ApiResponse<BlocklistRule[]>> {
    return this.client.get('/blocklist');
  }

  async createBlocklistRule(data: {
    field_name: string;
    match_value: string;
    match_type: string;
    scope: string;
    reason?: string;
  }): Promise<ApiResponse<BlocklistRule>> {
    return this.client.post('/blocklist', data);
  }

  async deleteBlocklistRule(id: string): Promise<ApiResponse<void>> {
    return this.client.delete(`/blocklist/${id}`);
  }

  async getPersonalBlocklistRules(): Promise<ApiResponse<BlocklistRule[]>> {
    return this.client.get('/blocklist/personal');
  }

  async createPersonalBlocklistRule(data: {
    field_name: string;
    match_value: string;
    match_type: string;
    reason?: string;
  }): Promise<ApiResponse<BlocklistRule>> {
    return this.client.post('/blocklist/personal', data);
  }

  async deletePersonalBlocklistRule(id: string): Promise<ApiResponse<void>> {
    return this.client.delete(`/blocklist/personal/${id}`);
  }

  // --- Email Templates ---
  async getEmailTemplates(params?: {
    template_key?: string;
    category?: string;
    locale?: string;
    organization_id?: string | null;
    include_inactive?: boolean;
  }): Promise<ApiResponse<EmailTemplate[]>> {
    return this.client.get('/email-templates', { params });
  }

  async getEmailTemplate(id: string): Promise<ApiResponse<EmailTemplate>> {
    return this.client.get(`/email-templates/${id}`);
  }

  async createEmailTemplate(data: {
    template_key: string;
    organization_id?: string | null;
    locale: string;
    name?: string | null;
    description?: string | null;
    category?: string | null;
    merge_schema_key?: string | null;
    subject_template: string;
    body_template: string;
    is_active?: boolean;
  }): Promise<ApiResponse<EmailTemplate>> {
    return this.client.post('/email-templates', data);
  }

  async updateEmailTemplate(id: string, data: Partial<{
    name: string | null;
    description: string | null;
    category: string | null;
    locale: string;
    merge_schema_key: string | null;
    subject_template: string;
    body_template: string;
    is_active: boolean;
  }>): Promise<ApiResponse<EmailTemplate>> {
    return this.client.patch(`/email-templates/${id}`, data);
  }

  async deleteEmailTemplate(id: string): Promise<ApiResponse<void>> {
    return this.client.delete(`/email-templates/${id}`);
  }

  async previewEmailTemplate(id: string, variables?: Record<string, any>): Promise<ApiResponse<EmailTemplatePreview>> {
    return this.client.post(`/email-templates/${id}/preview`, { variables });
  }

  async getEmailMergeSchemas(): Promise<ApiResponse<EmailTemplateMergeSchema[]>> {
    return this.client.get('/email-templates/merge-schemas');
  }

  async getEmailMergeSchema(key: string): Promise<ApiResponse<EmailTemplateMergeSchema>> {
    return this.client.get(`/email-templates/merge-schemas/${encodeURIComponent(key)}`);
  }

  async uploadEmailTemplateAsset(
    templateId: string,
    file: File,
    onProgress?: (pct: number) => void
  ): Promise<ApiResponse<EmailTemplateAsset>> {
    const formData = new FormData();
    formData.append('file', file);
    return this.client.post(`/email-templates/${templateId}/assets`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      onUploadProgress: (e) => onProgress && onProgress(Math.round((e.loaded * 100) / (e.total || 1))),
    });
  }

  // --- SLA / Reports ---
  async getSlaCompliance(): Promise<ApiResponse<any>> {
    return this.client.get('/reports/sla-compliance');
  }

  // --- Organization SLA update ---
  async updateOrganizationSla(id: string, data: {
    sla_response_hours?: number;
    sla_resolution_hours?: number;
    business_hours_start?: string;
    business_hours_end?: string;
    business_days?: number[];
  }): Promise<ApiResponse<any>> {
    return this.client.patch(`/organizations/${id}`, data);
  }

  // --- System Settings ---
  async getSystemSettings(): Promise<ApiResponse<any>> {
    return this.client.get('/settings/system');
  }

  async updateSystemSettings(category: string, settings: Record<string, string>): Promise<ApiResponse<any>> {
    return this.client.patch('/settings/system', { category, settings });
  }

  async testSmtp(data: { host: string; port: number; secure: boolean; user: string; pass: string }): Promise<ApiResponse<any>> {
    return this.client.post('/settings/system/test-smtp', data);
  }

  async testAi(data: { provider: string; apiKey: string; model?: string }): Promise<ApiResponse<any>> {
    return this.client.post('/settings/system/test-ai', data);
  }

  async testTranscription(data: { provider: string; apiKey: string }): Promise<ApiResponse<any>> {
    return this.client.post('/settings/system/test-transcription', data);
  }

  // --- Deploy ---
  async getDeployHistory(): Promise<ApiResponse<any>> {
    return this.client.get('/settings/deploy/history');
  }

  async getCurrentVersion(): Promise<ApiResponse<any>> {
    return this.client.get('/settings/deploy/current-version');
  }

  // --- Attendance ---
  async clockIn(notes?: string, location_type?: string): Promise<ApiResponse<AttendanceRecord>> {
    return this.client.post('/attendance/clock-in', { notes, location_type });
  }

  async clockOut(notes?: string): Promise<ApiResponse<AttendanceRecord>> {
    return this.client.post('/attendance/clock-out', { notes });
  }

  async startBreak(): Promise<ApiResponse<AttendanceRecord>> {
    return this.client.post('/attendance/break-start');
  }

  async endBreak(): Promise<ApiResponse<AttendanceRecord>> {
    return this.client.post('/attendance/break-end');
  }

  async getAttendanceStatus(): Promise<ApiResponse<AttendanceStatus>> {
    return this.client.get('/attendance/status');
  }

  async getMyDailyAttendance(date: string): Promise<ApiResponse<{ events: AttendanceRecord[]; summary: AttendanceDailySummary }>> {
    return this.client.get(`/attendance/my/daily?date=${date}`);
  }

  async getMyMonthlyAttendance(month: string): Promise<ApiResponse<{ summaries: AttendanceDailySummary[]; events_by_date: Record<string, AttendanceRecord[]> }>> {
    return this.client.get(`/attendance/my/monthly?month=${month}`);
  }

  async confirmAttendance(date: string): Promise<ApiResponse<AttendanceDailySummary>> {
    return this.client.post(`/attendance/my/confirm?date=${date}`);
  }

  async disputeAttendance(date: string, notes: string): Promise<ApiResponse<AttendanceDailySummary>> {
    return this.client.post(`/attendance/my/dispute?date=${date}`, { notes });
  }

  async getUserDailyAttendance(userId: string, date: string): Promise<ApiResponse<{ events: AttendanceRecord[]; summary: AttendanceDailySummary }>> {
    return this.client.get(`/attendance/users/${userId}/daily?date=${date}`);
  }

  async getUserMonthlyAttendance(userId: string, month: string): Promise<ApiResponse<{ summaries: AttendanceDailySummary[]; events_by_date: Record<string, AttendanceRecord[]> }>> {
    return this.client.get(`/attendance/users/${userId}/monthly?month=${month}`);
  }

  async adjustAttendance(userId: string, data: { event_type: string; recorded_at: string; reason: string }): Promise<ApiResponse<AttendanceRecord>> {
    return this.client.post(`/attendance/users/${userId}/adjust`, data);
  }

  async managerConfirmAttendance(userId: string, date: string): Promise<ApiResponse<AttendanceDailySummary>> {
    return this.client.post(`/attendance/users/${userId}/manager-confirm?date=${date}`);
  }

  async getAttendanceReport(month: string, orgId?: string): Promise<ApiResponse<any>> {
    const params = orgId ? `?month=${month}&org_id=${orgId}` : `?month=${month}`;
    return this.client.get(`/attendance/report${params}`);
  }

  async exportAttendanceCSV(month: string, orgId?: string): Promise<Blob> {
    const params = orgId ? `?month=${month}&org_id=${orgId}` : `?month=${month}`;
    const response = await this.client.get(`/attendance/export${params}`, { responseType: 'blob' });
    return response as any;
  }

  async getAttendancePolicies(orgId?: string): Promise<ApiResponse<AttendancePolicy[]>> {
    const params = orgId ? `?org_id=${orgId}` : '';
    return this.client.get(`/attendance/policies${params}`);
  }

  async createAttendancePolicy(data: Partial<AttendancePolicy> & { organization_id: string }): Promise<ApiResponse<AttendancePolicy>> {
    return this.client.post('/attendance/policies', data);
  }

  async updateAttendancePolicy(id: string, data: Partial<AttendancePolicy>): Promise<ApiResponse<AttendancePolicy>> {
    return this.client.patch(`/attendance/policies/${id}`, data);
  }

  async deleteAttendancePolicy(id: string): Promise<ApiResponse<void>> {
    return this.client.delete(`/attendance/policies/${id}`);
  }

  async getAttendanceVsTickets(month: string): Promise<ApiResponse<AttendanceVsTicketsRow[]>> {
    return this.client.get(`/reports/attendance-vs-tickets?month=${month}`);
  }

  // --- Push Notifications ---
  async subscribePush(data: { endpoint: string; keys: { p256dh: string; auth: string } }): Promise<ApiResponse<void>> {
    return this.client.post('/push/subscribe', data);
  }

  async unsubscribePush(endpoint: string): Promise<ApiResponse<void>> {
    return this.client.delete('/push/unsubscribe', { data: { endpoint } });
  }

  async getVapidPublicKey(): Promise<ApiResponse<{ publicKey: string }>> {
    return this.client.get('/push/vapid-key');
  }

  // --- Dashboard Widget Preferences ---
  async updateDashboardPreferences(preferences: string[]): Promise<ApiResponse<any>> {
    return this.client.patch('/profile/dashboard-preferences', { dashboard_widget_preferences: preferences });
  }

  // --- Dashboard Builder ---
  async getDashboards(): Promise<ApiResponse<Dashboard[]>> {
    return this.client.get('/dashboard-builder');
  }

  async getDashboard(id: string): Promise<ApiResponse<Dashboard>> {
    return this.client.get(`/dashboard-builder/${id}`);
  }

  async getDefaultDashboard(): Promise<ApiResponse<Dashboard>> {
    return this.client.get('/dashboard-builder/default/init');
  }

  async createDashboard(data: { name?: string; is_default?: boolean }): Promise<ApiResponse<Dashboard>> {
    return this.client.post('/dashboard-builder', data);
  }

  async updateDashboard(id: string, data: { name?: string; is_default?: boolean; config?: Record<string, any> }): Promise<ApiResponse<Dashboard>> {
    return this.client.patch(`/dashboard-builder/${id}`, data);
  }

  async deleteDashboard(id: string): Promise<ApiResponse<{ deleted: boolean }>> {
    return this.client.delete(`/dashboard-builder/${id}`);
  }

  async duplicateDashboard(id: string): Promise<ApiResponse<Dashboard>> {
    return this.client.post(`/dashboard-builder/${id}/duplicate`);
  }

  async reorderDashboards(order: { id: string; sort_order: number }[]): Promise<ApiResponse<{ updated: boolean }>> {
    return this.client.patch('/dashboard-builder/reorder/batch', { order });
  }

  async saveDashboardWidgets(dashboardId: string, widgets: any[]): Promise<ApiResponse<DashboardWidget[]>> {
    return this.client.put(`/dashboard-builder/${dashboardId}/widgets`, { widgets });
  }

  async addWidget(dashboardId: string, widget: any): Promise<ApiResponse<DashboardWidget>> {
    return this.client.post(`/dashboard-builder/${dashboardId}/widgets`, widget);
  }

  async removeWidget(dashboardId: string, widgetId: string): Promise<ApiResponse<{ deleted: boolean }>> {
    return this.client.delete(`/dashboard-builder/${dashboardId}/widgets/${widgetId}`);
  }

  async getWidgetCatalog(): Promise<ApiResponse<WidgetCatalogItem[]>> {
    return this.client.get('/dashboard-builder/widget-catalog');
  }

  // --- Contacts CRM ---
  async getContacts(params?: { organization_id?: string; search?: string; limit?: number; offset?: number }): Promise<ApiResponse<Contact[]>> {
    return this.client.get('/contacts', { params });
  }

  async getContact(id: string): Promise<ApiResponse<Contact>> {
    return this.client.get(`/contacts/${id}`);
  }

  async createContact(data: Partial<Contact> & { organization_id: string; name: string }): Promise<ApiResponse<Contact>> {
    return this.client.post('/contacts', data);
  }

  async updateContact(id: string, data: Partial<Contact>): Promise<ApiResponse<Contact>> {
    return this.client.patch(`/contacts/${id}`, data);
  }

  async deleteContact(id: string): Promise<ApiResponse<void>> {
    return this.client.delete(`/contacts/${id}`);
  }

  async importContacts(file: File, organizationId: string): Promise<ApiResponse<Contact[]>> {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('organization_id', organizationId);
    return this.client.post('/contacts/import', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  }

  async getContactInteractions(contactId: string): Promise<ApiResponse<ContactInteraction[]>> {
    return this.client.get(`/contacts/${contactId}/interactions`);
  }

  async createContactInteraction(contactId: string, data: { interaction_type: string; summary?: string; ticket_id?: string }): Promise<ApiResponse<ContactInteraction>> {
    return this.client.post(`/contacts/${contactId}/interactions`, data);
  }

  // --- Dashboard Managed Clients ---
  async getManagedClients(): Promise<ApiResponse<any[]>> {
    return this.client.get('/dashboard/managed-clients');
  }

  // --- Daily Status ---
  async getDailyStatus(): Promise<ApiResponse<any>> {
    return this.client.get('/dashboard/daily-status');
  }

  async getRecentConversations(limit?: number): Promise<ApiResponse<any[]>> {
    return this.client.get('/dashboard/recent-conversations', { params: { limit } });
  }

  // --- Morning Brief ---
  async getMorningBrief(): Promise<ApiResponse<any>> {
    return this.client.get('/dashboard/morning-brief');
  }

  // --- Client Energy ---
  async getClientEnergy(): Promise<ApiResponse<any[]>> {
    return this.client.get('/reports/client-energy');
  }

  // --- Cash Flow ---
  async getCashFlowEntries(params?: { month?: string; type?: string }): Promise<ApiResponse<any[]>> {
    const q = params ? new URLSearchParams(params as any).toString() : '';
    return this.client.get(`/cashflow${q ? '?' + q : ''}`);
  }

  async createCashFlowEntry(data: any): Promise<ApiResponse<any>> {
    return this.client.post('/cashflow', data);
  }

  async updateCashFlowEntry(id: string, data: any): Promise<ApiResponse<any>> {
    return this.client.patch(`/cashflow/${id}`, data);
  }

  async deleteCashFlowEntry(id: string): Promise<ApiResponse<void>> {
    return this.client.delete(`/cashflow/${id}`);
  }

  async getCashFlowProjection(months?: number): Promise<ApiResponse<any>> {
    return this.client.get(`/cashflow/projection${months ? '?months=' + months : ''}`);
  }

  // --- Backups ---
  async getBackups(params?: { limit?: number; offset?: number; status?: string }): Promise<ApiResponse<any>> {
    return this.client.get('/settings/backups', { params });
  }

  async getBackupStats(): Promise<ApiResponse<any>> {
    return this.client.get('/settings/backups/stats');
  }

  async getBackup(id: string): Promise<ApiResponse<any>> {
    return this.client.get(`/settings/backups/${id}`);
  }

  async deleteBackup(id: string): Promise<ApiResponse<void>> {
    return this.client.delete(`/settings/backups/${id}`);
  }

  async verifyBackup(id: string): Promise<ApiResponse<{ verified: boolean }>> {
    return this.client.post(`/settings/backups/${id}/verify`);
  }

  async downloadBackup(id: string): Promise<void> {
    const response = await this.client.get(`/settings/backups/${id}/download`, {
      responseType: 'blob',
    });
    const blob = new Blob([response.data as any]);
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `backup_${id}.tar.gz`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  }

  // --- Backup Schedules ---
  async getBackupSchedules(): Promise<ApiResponse<any>> {
    return this.client.get('/settings/backups/schedules/list');
  }

  async createBackupSchedule(data: any): Promise<ApiResponse<any>> {
    return this.client.post('/settings/backups/schedules', data);
  }

  async updateBackupSchedule(id: string, data: any): Promise<ApiResponse<any>> {
    return this.client.patch(`/settings/backups/schedules/${id}`, data);
  }

  async deleteBackupSchedule(id: string): Promise<ApiResponse<void>> {
    return this.client.delete(`/settings/backups/schedules/${id}`);
  }

  // --- Backup Storage ---
  async getBackupStorageConfigs(): Promise<ApiResponse<any>> {
    return this.client.get('/settings/backups/storage/list');
  }

  async createBackupStorageConfig(data: any): Promise<ApiResponse<any>> {
    return this.client.post('/settings/backups/storage', data);
  }

  async updateBackupStorageConfig(id: string, data: any): Promise<ApiResponse<any>> {
    return this.client.patch(`/settings/backups/storage/${id}`, data);
  }

  async deleteBackupStorageConfig(id: string): Promise<ApiResponse<void>> {
    return this.client.delete(`/settings/backups/storage/${id}`);
  }

  async testBackupStorageConfig(id: string): Promise<ApiResponse<{ connected: boolean }>> {
    return this.client.post(`/settings/backups/storage/${id}/test`);
  }

  // --- Leads ---
  async getLeads(params?: { status?: string; source?: string; assigned_to?: string; search?: string; limit?: number; offset?: number }): Promise<ApiResponse<Lead[]>> {
    return this.client.get('/leads', { params });
  }

  async getLead(id: string): Promise<ApiResponse<Lead>> {
    return this.client.get(`/leads/${id}`);
  }

  async createLead(data: Partial<Lead>): Promise<ApiResponse<Lead>> {
    return this.client.post('/leads', data);
  }

  async updateLead(id: string, data: Partial<Lead>): Promise<ApiResponse<Lead>> {
    return this.client.patch(`/leads/${id}`, data);
  }

  async deleteLead(id: string): Promise<ApiResponse<void>> {
    return this.client.delete(`/leads/${id}`);
  }

  async convertLead(id: string): Promise<ApiResponse<{ organization_id: string }>> {
    return this.client.post(`/leads/${id}/convert`);
  }

  async addLeadCommunication(leadId: string, data: { call_status: string; summary?: string; next_communication?: string; communication_date?: string }): Promise<ApiResponse<LeadCommunication>> {
    return this.client.post(`/leads/${leadId}/communications`, data);
  }

  async getLeadCommunications(leadId: string): Promise<ApiResponse<LeadCommunication[]>> {
    return this.client.get(`/leads/${leadId}/communications`);
  }

  async addLeadProduct(leadId: string, data: { product_name: string; product_id?: string; quantity?: number; unit_price?: number; vat_rate?: number; notes?: string }): Promise<ApiResponse<LeadProduct>> {
    return this.client.post(`/leads/${leadId}/products`, data);
  }

  async removeLeadProduct(leadId: string, productId: string): Promise<ApiResponse<void>> {
    return this.client.delete(`/leads/${leadId}/products/${productId}`);
  }

  // --- Products ---
  async getProducts(params?: { active?: string; search?: string; category?: string; limit?: number; offset?: number }): Promise<ApiResponse<Product[]>> {
    return this.client.get('/products', { params });
  }

  async createProduct(data: Partial<Product>): Promise<ApiResponse<Product>> {
    return this.client.post('/products', data);
  }

  async updateProduct(id: string, data: Partial<Product>): Promise<ApiResponse<Product>> {
    return this.client.patch(`/products/${id}`, data);
  }

  async deleteProduct(id: string): Promise<ApiResponse<void>> {
    return this.client.delete(`/products/${id}`);
  }

  // --- Quotes ---
  async getQuotes(params?: { organization_id?: string; lead_id?: string; status?: string; quote_type?: string; limit?: number; offset?: number }): Promise<ApiResponse<Quote[]>> {
    return this.client.get('/quotes', { params });
  }

  async getQuote(id: string): Promise<ApiResponse<Quote>> {
    return this.client.get(`/quotes/${id}`);
  }

  async createQuote(data: Partial<Quote>): Promise<ApiResponse<Quote>> {
    return this.client.post('/quotes', data);
  }

  async updateQuote(id: string, data: Partial<Quote>): Promise<ApiResponse<Quote>> {
    return this.client.patch(`/quotes/${id}`, data);
  }

  async sendQuote(id: string): Promise<ApiResponse<Quote>> {
    return this.client.post(`/quotes/${id}/send`);
  }

  async approveQuote(id: string): Promise<ApiResponse<Quote>> {
    return this.client.post(`/quotes/${id}/approve`);
  }

  async rejectQuote(id: string, reason?: string): Promise<ApiResponse<Quote>> {
    return this.client.post(`/quotes/${id}/reject`, { rejection_reason: reason });
  }

  async duplicateQuote(id: string): Promise<ApiResponse<Quote>> {
    return this.client.post(`/quotes/${id}/duplicate`);
  }

  async addQuoteItem(quoteId: string, data: Partial<QuoteLineItem>): Promise<ApiResponse<QuoteLineItem>> {
    return this.client.post(`/quotes/${quoteId}/items`, data);
  }

  async updateQuoteItem(quoteId: string, itemId: string, data: Partial<QuoteLineItem>): Promise<ApiResponse<QuoteLineItem>> {
    return this.client.patch(`/quotes/${quoteId}/items/${itemId}`, data);
  }

  async deleteQuoteItem(quoteId: string, itemId: string): Promise<ApiResponse<void>> {
    return this.client.delete(`/quotes/${quoteId}/items/${itemId}`);
  }

  // --- Lead Pipeline Dashboard ---
  async getLeadPipeline(): Promise<ApiResponse<{ status: string; count: number; total_value: number }[]>> {
    return this.client.get('/dashboard/lead-pipeline');
  }

  async getProjectHealth(orgId?: string): Promise<ApiResponse<any[]>> {
    const params = orgId ? `?org_id=${orgId}` : '';
    return this.client.get(`/reports/project-health${params}`);
  }

  async getAttendanceVsLogs(month?: string): Promise<ApiResponse<any[]>> {
    const params = month ? `?month=${month}` : '';
    return this.client.get(`/reports/attendance-vs-logs${params}`);
  }

  async getMyMonthlySummary(monthYear: string): Promise<ApiResponse<any>> {
    return this.client.get(`/attendance/monthly/my/${monthYear}`);
  }

  async getMonthlySummary(userId: string, monthYear: string): Promise<ApiResponse<any>> {
    return this.client.get(`/attendance/monthly/${userId}/${monthYear}`);
  }

  async generateMonthlySummary(userId: string, monthYear: string): Promise<ApiResponse<any>> {
    return this.client.post(`/attendance/monthly/${userId}/${monthYear}/generate`);
  }

  async confirmMyAttendanceMonthly(monthYear: string): Promise<ApiResponse<any>> {
    return this.client.post(`/attendance/monthly/my/${monthYear}/confirm`);
  }

  async confirmMyLogsMonthly(monthYear: string): Promise<ApiResponse<any>> {
    return this.client.post(`/attendance/monthly/my/${monthYear}/confirm-logs`);
  }

  async managerConfirmAttendanceMonthly(userId: string, monthYear: string): Promise<ApiResponse<any>> {
    return this.client.post(`/attendance/monthly/${userId}/${monthYear}/manager-confirm`);
  }

  async sendLogsForReviewMonthly(userId: string, monthYear: string): Promise<ApiResponse<any>> {
    return this.client.post(`/attendance/monthly/${userId}/${monthYear}/send-logs`);
  }

  // Meetings
  async getMeetings(params?: { from?: string; to?: string; status?: string }): Promise<ApiResponse<any[]>> {
    const q = new URLSearchParams(params as any).toString();
    return this.client.get(`/meetings${q ? '?' + q : ''}`);
  }

  async getUpcomingMeetings(): Promise<ApiResponse<any[]>> {
    return this.client.get('/meetings/upcoming');
  }

  async getMeeting(id: string): Promise<ApiResponse<any>> {
    return this.client.get(`/meetings/${id}`);
  }

  async createMeeting(data: any): Promise<ApiResponse<any>> {
    return this.client.post('/meetings', data);
  }

  async updateMeeting(id: string, data: any): Promise<ApiResponse<any>> {
    return this.client.patch(`/meetings/${id}`, data);
  }

  async deleteMeeting(id: string): Promise<ApiResponse<any>> {
    return this.client.delete(`/meetings/${id}`);
  }

  async completeMeeting(id: string, data?: { follow_up_date?: string; follow_up_notes?: string }): Promise<ApiResponse<any>> {
    return this.client.patch(`/meetings/${id}/complete`, data || {});
  }

  async addMeetingParticipant(meetingId: string, data: { contact_id?: string; user_id?: string }): Promise<ApiResponse<any>> {
    return this.client.post(`/meetings/${meetingId}/participants`, data);
  }

  async removeMeetingParticipant(meetingId: string, participantId: string): Promise<ApiResponse<any>> {
    return this.client.delete(`/meetings/${meetingId}/participants/${participantId}`);
  }

  // Checklist
  async getChecklist(ticketId: string): Promise<ApiResponse<any[]>> {
    return this.client.get(`/tickets/${ticketId}/checklist`);
  }

  async addChecklistItem(ticketId: string, text: string): Promise<ApiResponse<any>> {
    return this.client.post(`/tickets/${ticketId}/checklist`, { text });
  }

  async updateChecklistItem(ticketId: string, itemId: string, data: { text?: string; completed?: boolean }): Promise<ApiResponse<any>> {
    return this.client.patch(`/tickets/${ticketId}/checklist/${itemId}`, data);
  }

  async deleteChecklistItem(ticketId: string, itemId: string): Promise<ApiResponse<any>> {
    return this.client.delete(`/tickets/${ticketId}/checklist/${itemId}`);
  }

  async reorderChecklist(ticketId: string, ids: string[]): Promise<ApiResponse<any>> {
    return this.client.post(`/tickets/${ticketId}/checklist/reorder`, { ids });
  }
}

export const api = new ApiService();
